import * as z from 'zod/mini';
import { NOTE_MAX_LENGTH } from '../constants.ts';
import { idSchema, NODE_TYPES, timestampSchema } from './common.ts';
import { shownName } from './user.ts';

// A note is one entry in a node's running log: who wrote it, when, and what
// they wrote. Notes are never edited — a correction is another note — so
// the log reads like a site diary. The author's username and display name
// are copied onto the row when it is written, so the entry still reads
// after the account is gone; an entry imported from the old free-text field
// has no author at all.

/** Every node a note can be about: the six kinds that had a notes field. */
export const NOTE_NODE_TYPES = NODE_TYPES;
export type NoteNodeType = (typeof NOTE_NODE_TYPES)[number];
export const noteNodeTypeSchema = z.enum(NOTE_NODE_TYPES);

export const noteSchema = z.object({
  id: idSchema,
  nodeType: noteNodeTypeSchema,
  nodeId: idSchema,
  /** Null for an imported note, or once the author's account is removed. */
  userId: z.nullable(idSchema),
  /** '' for an imported note. */
  username: z.string(),
  /** The author's display name when the note was written; '' when they had none. */
  displayName: z.string(),
  body: z.string(),
  createdAt: timestampSchema,
});
export type Note = z.infer<typeof noteSchema>;

export const noteInputSchema = z.object({
  body: z
    .string()
    .check(z.trim(), z.minLength(1, 'Write something first'), z.maxLength(NOTE_MAX_LENGTH)),
});
export type NoteInput = z.infer<typeof noteInputSchema>;

export const noteEnvelopeSchema = z.object({ note: noteSchema });

export const noteListSchema = z.object({
  /** Newest first. */
  notes: z.array(noteSchema),
});

/** What an imported note (no author) is called where a name would go. */
const IMPORTED_NOTE_AUTHOR = 'earlier note';

/** The author as shown for an account (display name, else username), or the imported label. */
export function noteAuthor(note: Pick<Note, 'username' | 'displayName'>): string {
  return note.username === '' ? IMPORTED_NOTE_AUTHOR : shownName(note);
}
